import { useMemo } from 'react'
import { useApi } from './useApi'
// eslint-disable-next-line import/no-unresolved
import { getCatalogProducts, getCatalogCategories, getCmsPages } from 'data-api'
import { addTrailingSlash, isFullURL } from '../utils'
import { useConfig } from '../providers/config'

const method = async ({ productEntityIds, categoryEntityIds, pageEntityIds, ...args }) => {
	let productsPromise
	let categoriesPromise
	let pagesPromise

	if (productEntityIds?.length) {
		productsPromise = getCatalogProducts({
			...args,
			where: {
				id: {
					inq: productEntityIds,
				},
			},
		})
	}
	if (categoryEntityIds?.length) {
		categoriesPromise = getCatalogCategories({
			...args,
			where: {
				id: {
					inq: categoryEntityIds,
				},
			},
		})
	}

	if (pageEntityIds?.length) {
		pagesPromise = getCmsPages({ ...args, where: { content_id: { inq: pageEntityIds } } })
	}

	const [{ data: products } = {}, { data: categories } = {}, { data: pages } = {}] = await Promise.all([
		productsPromise,
		categoriesPromise,
		pagesPromise,
	])

	return { data: { products, categories, pages } }
}

/**
 * Hook to get the links for the given entities.
 * @param {Array} entities - Array of entities to get links for. Each entity should have a type and href.
 * @param {Object} options - Options for the hook.
 * @param {boolean} options.paused - If true, the hook will not fetch data.
 * @returns {Array} - Array of links for the given entities.
 */
export const useLinks = (entities, { paused } = {}) => {
	const {
		catalog: { urlKeySuffix },
	} = useConfig()

	const { productEntityIds, categoryEntityIds, pageEntityIds } = useMemo(() => {
		const productEntityIds = []
		const categoryEntityIds = []
		const pageEntityIds = []

		entities.forEach(({ type, href }) => {
			if (type === 'product') {
				productEntityIds.push(href)
			} else if (type === 'category') {
				categoryEntityIds.push(href)
			} else if (type === 'page') {
				pageEntityIds.push(href)
			}
		})

		return { productEntityIds, categoryEntityIds, pageEntityIds }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(entities)])

	const { data = {} } = useApi({
		key: 'useLinks',
		method,
		args: { productEntityIds, categoryEntityIds, pageEntityIds },
		paused: paused ?? (!productEntityIds?.length && !categoryEntityIds?.length && !pageEntityIds?.length),
	})

	const links = useMemo(() => {
		const links = []
		const { products = [], categories = [], pages = [] } = data

		products.forEach(({ id, urlKey }) => {
			links[entities.findIndex(({ href }) => href === id)] = urlKey ? `/${urlKey}${urlKeySuffix ?? '/'}` : undefined
		})
		categories.forEach(({ id, path }) => {
			links[entities.findIndex(({ href }) => href === id)] = path
		})
		pages.forEach(({ id, urlKey }) => {
			links[entities.findIndex(({ href }) => href === id)] = `/${urlKey}/`
		})

		entities.forEach(({ type, href }, i) => {
			if (type === 'default' || (!!href && type !== 'product' && type !== 'category' && type !== 'page')) {
				let staticLink
				if (isFullURL(href)) {
					staticLink = href
				} else {
					// Add slashes if missing
					staticLink = addTrailingSlash(href)
				}
				links[i] = staticLink
			}
		})

		return links
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, JSON.stringify(entities), urlKeySuffix])

	return links
}
